export interface EducationItem {
  title: string;
  content: string;
  codeExample?: string;
}

export interface EducationCategory {
  id: string;
  title: string;
  icon: string;
  items: EducationItem[];
}

export const educationData: EducationCategory[] = [
  {
    id: 'tutorial',
    title: 'Tutorial',
    icon: 'T',
    items: [
      {
        title: 'Create Your First Post',
        content: 'With the Brain Dump feature, just write down whatever is on your mind and the AI will generate 5 tweet drafts for you.\n\n1. Select "Brain Dump" from the left menu\n2. Write your thoughts in the text area\n3. Click "Generate Tweet Drafts"\n4. Click on a draft you like to copy it\n5. Paste it into Twitter and post!',
      },
      {
        title: 'Use AI Analysis',
        content: 'Algo Analyzer diagnoses how likely your post is to go viral using 8 metrics.\n\nMetrics:\n- Hook Strength (how well the opening grabs attention)\n- Clarity (how clear the message is)\n- Engagement Potential (how likely it is to get reactions)\n- Curiosity Gap (how much it makes readers want to read more)\n- Emotional Impact (emotional resonance)\n- Shareability (how likely it is to be shared)\n- Trend Alignment (relevance to current trends)\n- Readability (ease of reading)',
      },
      {
        title: 'Get Content Coaching',
        content: 'The Content Coach feature lets the AI analyze your existing posts and provide specific improvement advice.\n\n1. Enter the post you want to improve\n2. Click "Get Coaching"\n3. See strengths, areas for improvement, and an improved version\n4. Use the improved version as-is, or rewrite it yourself using the advice',
      },
      {
        title: 'Build Ideas',
        content: 'The Compose Ideas feature lets you combine multiple idea blocks to create a post.\n\n1. Click "+ Add Block" to add an idea block\n2. Enter fragments of your idea in each block\n3. Click "Merge Blocks" to combine into one text\n4. Click "Generate Variations" for AI-suggested rewrites',
      },
    ],
  },
  {
    id: 'tips',
    title: 'Growth Tips',
    icon: 'G',
    items: [
      {
        title: 'The Formula for Viral Tweets',
        content: '1. Hook in the first 3 seconds (stop the scroll)\n2. One tweet, one message (don\'t say multiple things)\n3. Use specific numbers ("a lot" -> "3x")\n4. Stir emotions (empathy, surprise, outrage)\n5. Prompt action (end with a question)',
      },
      {
        title: 'Best Times to Post',
        content: 'Peak engagement times on Twitter:\n\nMorning: 7:00-9:00 AM (commute time)\nLunch: 12:00-1:00 PM (lunch break)\nEvening: 8:00-10:00 PM (wind-down time)\n\nTech content performs especially well in the morning and evening. Weekdays tend to get higher engagement than weekends.',
      },
      {
        title: 'Tips for Growing Your Followers',
        content: '1. Optimize your profile (make it clear who you are and what you post about)\n2. Post consistently around a theme (tech, life hacks, etc.)\n3. Post 1-3 tweets per day\n4. Reply to other people\'s tweets (use the Reply Guy feature)\n5. Share deep insights in thread format\n6. Regularly analyze your viral posts (use the Account Researcher feature)',
      },
      {
        title: 'How to Write Effective Replies',
        content: 'Traits of a good reply:\n\n1. Specifically reference the original tweet\'s content\n2. Add your own experience or insight\n3. Expand the conversation with a question\n4. Not too short, not too long (1-2 sentences)\n5. Keep the tone positive\n\nReplies to avoid:\n- One-word replies like "Nice!"\n- Sneaking in self-promotion\n- Content unrelated to the original tweet',
      },
      {
        title: 'How to Use Hashtags',
        content: 'Effective hashtag strategy:\n\n1. Limit to 1-2 hashtags per tweet\n2. Combine popular tags with niche tags in your area\n3. Weave them naturally into the tweet text (don\'t just list them at the end)\n4. If jumping on a trending tag, make sure it\'s actually relevant\n\nRecommended tech hashtags: #coding #developer #webdev',
      },
    ],
  },
  {
    id: 'faq',
    title: 'FAQ',
    icon: 'Q',
    items: [
      {
        title: 'Where can I change the AI settings?',
        content: 'You can change them from "Admin Panel" in the left menu.\n\nSupported providers:\n- Groq (recommended: fast with a free tier)\n- Gemini (Google AI)\n- Together (high accuracy)\n- Claude (Anthropic)\n- OpenAI (GPT-4o Mini)\n\nGet your API key from each provider\'s website.',
      },
      {
        title: 'Where is my data stored?',
        content: 'All data is stored in your browser\'s localStorage.\n\nDetails:\n- Nothing is ever sent to a server\n- Data is lost if you clear your browser data\n- Storage limit: approximately 5MB\n- Only the latest 100 history entries are kept\n\nWe recommend exporting important data from the Admin Panel.',
      },
      {
        title: 'My API key is showing as invalid',
        content: 'Please check the following:\n\n1. Is the correct provider selected in the Admin Panel?\n2. Is the API key entered correctly (no extra spaces from copy-paste)?\n3. Has the API key expired?\n4. Have you hit the API usage limit?\n\nGroq has a free tier, so we recommend trying Groq first.',
      },
      {
        title: 'How can I improve the quality of generated tweets?',
        content: '1. Enter specific content in Brain Dump (the more detail, the better the results)\n2. Regenerate your style model (train with more tweet samples)\n3. Check improvement suggestions with Content Coach\n4. Improve low-scoring areas identified by Algo Analyzer\n5. Generate multiple times and pick the best draft',
      },
    ],
  },
];
